/* The theme's one publisher. Reads the DOM, publishes notch:* events on
 * Shopify's event bus; the custom pixel decides what leaves the page. No
 * request, no storage. One cookie, notch_env, and only on an unpublished
 * theme, so the pixel can tell a preview from a shopper. Event names and
 * keys are the list in store/tests/fixtures/notch-events.json.
 */
(() => {
  const queue = [];
  let bus = null;
  const flush = () => { while (queue.length) bus.publish(...queue.shift()); };
  const waitForBus = (tries = 0) => {
    bus = window.Shopify && window.Shopify.analytics;
    if (bus && typeof bus.publish === 'function') { flush(); return; }
    bus = null;
    if (tries < 80) setTimeout(() => waitForBus(tries + 1), 250);
  };

  const body = document.body;
  // Shopify's own preview cookie is not readable, so the theme says so itself.
  const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
  if (role && role !== 'main') document.cookie = 'notch_env=dev; path=/; max-age=86400; SameSite=Lax';
  const slug = (s) => String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
  const sectionOf = (el) => {
    const wrap = el && el.closest ? el.closest('.shopify-section') : null;
    if (!wrap) return {};
    const inner = [...wrap.children].find((c) => !/^(STYLE|SCRIPT|LINK|TEMPLATE)$/.test(c.tagName));
    const type = inner && [...inner.classList].find((c) => c.startsWith('notch-'));
    return {
      section_type: type || wrap.id.replace('shopify-section-', ''),
      section_id: (inner && (inner.dataset.section || inner.id)) || type || wrap.id,
    };
  };
  const publish = (name, payload, el) => {
    const data = Object.assign({ surface: body.dataset.notchSurface, test: body.dataset.notchTest || undefined }, sectionOf(el), payload);
    Object.keys(data).forEach((k) => { if (data[k] === undefined || data[k] === null || data[k] === '') delete data[k]; });
    if (bus) bus.publish(name, data); else queue.push([name, data]);
  };

  // Cart figures read from the rendered cart, never fetched.
  const money = (text) => { const n = parseFloat(String(text || '').replace(/[^0-9.]/g, '')); return isFinite(n) ? n : undefined; };
  const cartFigures = (scope) => {
    const root = scope || document;
    const lines = root.querySelectorAll('[data-notch-line-index]').length;
    const sub = root.querySelector('.notch-cart__subtotal span:last-child');
    return { line_count: lines || undefined, cart_total: sub ? money(sub.textContent) : undefined };
  };
  const locationOf = (el) => {
    if (el.closest('[data-notch-cart-drawer]')) return 'drawer';
    if (el.closest('.notch-cart')) return 'cart';
    if (el.closest('.notch-buy__sticky')) return 'sticky_bar';
    if (el.closest('.notch-buy')) return 'buy_box';
    return 'other';
  };

  // ---- clicks: CTAs, the checkout click, tabs, gallery thumbs
  const CTA = '.notch-btn, .button, .notch-header__link, .notch-header__cta, .notch-nav__link';
  const CHECKOUT = '.notch-buy__cta, .notch-drawer__checkout, .notch-cart__checkout';
  let lastClick = { at: 0, el: null };
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    lastClick = { at: Date.now(), el: t };

    const checkout = t.closest(CHECKOUT);
    if (checkout) {
      const form = checkout.closest('form') || checkout.closest('[data-notch-cart-drawer]') || checkout.closest('.notch-cart');
      publish('notch:checkout_clicked', Object.assign({ location: locationOf(checkout) }, cartFigures(form)), checkout);
      return;
    }
    const tab = t.closest('.notch-tabs__tab[role="tab"]');
    if (tab) {
      const tabs = [...tab.parentElement.querySelectorAll('[role="tab"]')];
      const prev = tabs.find((x) => x.getAttribute('aria-selected') === 'true');
      publish('notch:tab_selected', { tab: slug(tab.dataset.notchTab || tab.textContent), index: tabs.indexOf(tab), previous_tab: prev ? slug(prev.dataset.notchTab || prev.textContent) : undefined }, tab);
      return;
    }
    const thumb = t.closest('.notch-buy__thumb');
    if (thumb) {
      const m = /-v(\d+)-slide-(\d+)$|-slide-(\d+)$/.exec(thumb.getAttribute('href') || '');
      publish('notch:gallery_viewed', { view: m && m[1] ? Number(m[1]) : undefined, slide_index: m ? Number(m[2] || m[3]) : undefined, method: 'thumb' }, thumb);
      return;
    }
    const cta = t.closest(CTA);
    if (!cta || cta.closest('[data-notch-cart-link]')) return;
    const href = cta.getAttribute('href') || undefined;
    publish('notch:cta_clicked', { label: cta.dataset.notchTrack || slug(cta.textContent), href }, cta);
    // A same-tab navigation unloads the page before the pixel sandbox has
    // sent the event. Hold the navigation for 250ms; the pixel sends at once.
    const navigates = cta.tagName === 'A' && href && !href.startsWith('#') && cta.target !== '_blank'
      && !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
      && cta.origin === location.origin;
    if (navigates) {
      e.preventDefault();
      onHide();
      setTimeout(() => { location.href = cta.href; }, 250);
    }
  }, true);

  // ---- gallery slides that scroll into view
  const slides = [...document.querySelectorAll('.notch-buy__slide')];
  if (slides.length && 'IntersectionObserver' in window) {
    let armed = false;
    setTimeout(() => { armed = true; }, 1500);
    const gallery = new IntersectionObserver((entries) => {
      if (!armed || Date.now() - lastClick.at < 1000) return;
      entries.filter((x) => x.isIntersecting).forEach((x) => {
        const m = /-v(\d+)-slide-(\d+)$|-slide-(\d+)$/.exec(x.target.id || '');
        if (m) publish('notch:gallery_viewed', { view: m[1] ? Number(m[1]) : undefined, slide_index: Number(m[2] || m[3]), method: 'scroll' }, x.target);
      });
    }, { threshold: 0.6 });
    slides.forEach((s) => gallery.observe(s));
  }

  // ---- buy box controls
  const previous = {};
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    // The gate's optional question. Published on change, not on submit, so an
    // answer from someone who then abandons the gate is still counted; the
    // tag on the customer record is the submitted half.
    if (t.dataset.notchAnswer) return publish('notch:gate_answered', { answer: t.dataset.notchAnswer }, t);
    if (t.dataset.notchAxis === 'flavour' || t.hasAttribute('data-swatch')) {
      const key = t.name;
      publish('notch:flavour_selected', { flavour: slug(t.value), previous_flavour: previous[key] }, t);
      previous[key] = slug(t.value);
      return;
    }
    if (t.name === 'selling_plan') {
      publish('notch:plan_selected', { plan: t.value ? 'subscribe' : 'one_time', selling_plan_id: t.value || undefined }, t);
      return;
    }
    if (t.dataset.notchAxis === 'size') {
      const root = t.closest('[data-notch-buy-box]');
      const id = root && root.querySelector('[data-notch-variant-input]');
      publish('notch:variant_selected', { variant_id: id ? id.value : undefined, pack_size: slug(t.value) }, t);
    }
  }, true);

  // ---- accordions (toggle does not bubble; capture it)
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    const siblings = [...d.parentElement.querySelectorAll(':scope > details')];
    const sum = d.querySelector('summary');
    publish('notch:accordion_opened', { pane: d.dataset.notchPane || slug(sum && sum.textContent), position: siblings.indexOf(d) + 1 }, d);
  }, true);

  // ---- cart drawer open/close and its error line
  let lastSubmit = 0;
  document.addEventListener('submit', (e) => { if (e.target.closest && e.target.closest('[data-notch-add-form]')) lastSubmit = Date.now(); }, true);
  const drawer = document.querySelector('[data-notch-cart-drawer]');
  if (drawer && 'MutationObserver' in window) {
    new MutationObserver((muts) => {
      muts.forEach((m) => {
        if (m.target === drawer && m.attributeName === 'open') {
          if (drawer.open) {
            const trigger = Date.now() - lastSubmit < 3000 ? 'add' : (lastClick.el && lastClick.el.closest('[data-notch-cart-link]') ? 'header' : 'other');
            publish('notch:cart_opened', Object.assign({ trigger }, cartFigures(drawer)), drawer);
          } else {
            const el = lastClick.el;
            const recent = Date.now() - lastClick.at < 1000;
            const method = recent && el && el.closest('[data-notch-drawer-close]') ? 'close_button' : (recent && el === drawer ? 'backdrop' : (recent && el && el.closest('a') ? 'link' : 'escape'));
            publish('notch:cart_closed', Object.assign({ method }, cartFigures(drawer)), drawer);
          }
        }
        if (m.attributeName === 'hidden' && m.target.matches && m.target.matches('[data-notch-drawer-error]') && !m.target.hidden && m.target.textContent.trim()) {
          publish('notch:error_shown', { message: m.target.textContent.trim().slice(0, 200) }, m.target);
        }
      });
    }).observe(drawer, { attributes: true, subtree: true, attributeFilter: ['open', 'hidden'] });
  }

  // ---- the pre-launch gate: closed without an email is a finding.
  // The gate opens and closes by :target, so the hash is its state.
  let gateOpenedAt = 0;
  let gateTrigger = 'click';
  const gateBucket = (ms) => (ms < 2000 ? '<2s' : ms < 6000 ? '2-6s' : ms < 15000 ? '6-15s' : '15s+');
  window.addEventListener('hashchange', (e) => {
    const was = (e.oldURL || '').includes('#notch-prelaunch');
    const is = (e.newURL || '').includes('#notch-prelaunch');
    const panel = document.querySelector('.notch-prelaunch');
    if (!was && is) {
      gateOpenedAt = Date.now();
      gateTrigger = (panel && panel.dataset.notchOpenedBy) || 'click';
      publish('notch:gate_opened', { trigger: gateTrigger }, panel);
      return;
    }
    if (was && !is && gateOpenedAt) {
      const el = lastClick.el;
      const recent = Date.now() - lastClick.at < 1000;
      const method = recent && el && el.closest('.notch-prelaunch__close') ? 'close_button'
        : (recent && el && el.closest('.notch-prelaunch__scrim') ? 'scrim' : 'back');
      publish('notch:gate_closed', { method, trigger: gateTrigger, dwell_bucket: gateBucket(Date.now() - gateOpenedAt) }, panel);
      gateOpenedAt = 0;
    }
  });

  // ---- the customer forms carry the browser id, so the Shopify record can
  // be linked to this browser's PostHog person server-side. The id is the
  // value the pixel already sends as distinct_id: Shopify's own _shopify_y
  // cookie. It rides in the tags input as `uid:<value>`, because tags are
  // the one field this form is known to persist. Stamped on load and again
  // at submit, once; the address itself is never published.
  const browserId = () => (document.cookie.match(/(?:^|; )_shopify_y=([^;]+)/) || [])[1];
  const stampUid = (f) => {
    const uid = browserId();
    if (!uid) return;
    // Every tags input, not the first: a checked answer radio is what posts.
    f.querySelectorAll('input[name="contact[tags]"]').forEach((tags) => {
      if (/(^|,)uid:/.test(tags.value)) return;
      tags.value = (tags.value ? tags.value + ',' : '') + 'uid:' + uid;
      tags.setAttribute('value', tags.value); // so the DOM reads what the post will send
    });
  };
  document.querySelectorAll('form input[name="contact[email]"]').forEach((el) => stampUid(el.form));

  // ---- the submit, at submit time, as an event with its location only.
  // The submit itself is NOT touched: Shopify's captcha hooks the submit
  // event to attach its token, and a held form.submit() skipped that hook
  // and killed the post with "Missing CAPTCHA token". The server-rendered
  // confirmation (email_captured) is the durable record.
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!(f instanceof HTMLFormElement) || e.defaultPrevented) return;
    const emailInput = f.querySelector('input[name="contact[email]"]');
    if (!emailInput || !emailInput.value) return;
    stampUid(f);
    // The native fallback re-dispatches submit; the event was already published.
    if (!f.dataset.notchNative) {
      const tag = f.querySelector('input[name="contact[tags]"]');
      const location = tag ? tag.value.split(',')[0] : undefined;
      publish('notch:email_submitted', { location }, f);
    }

    // The gate submits in place: the redirect round trip loses the modal, so
    // a shopper never saw the confirmation. Fetch the post; on success swap
    // in the success template, celebrate, and record the one capture. On any
    // failure, requestSubmit() re-runs the native path with every submit
    // hook (Shopify's captcha included) intact.
    if (f.dataset.notchNative || !f.classList.contains('notch-prelaunch__form') || !window.fetch) return;
    const template = f.querySelector('template[data-notch-success]');
    if (!template) return;
    e.preventDefault();
    fetch(f.getAttribute('action') || '/contact', { method: 'POST', body: new FormData(f), redirect: 'follow' })
      .then((res) => {
        if (!res.ok || !/customer_posted=true/.test(res.url)) throw new Error('not posted');
        const panel = f.querySelector('.notch-prelaunch__panel');
        [...panel.querySelectorAll('.notch-prelaunch__title, .notch-prelaunch__capture, .notch-field, .notch-btn, .notch-prelaunch__consent')].forEach((el) => { el.hidden = true; });
        panel.querySelector('.notch-prelaunch__body').replaceWith(template.content.cloneNode(true));
        publish('notch:email_captured', { location: 'prelaunch' }, f);
        if (typeof window.notchCelebrate === 'function') window.notchCelebrate();
      })
      .catch(() => { f.dataset.notchNative = '1'; f.requestSubmit(); });
  }, true);

  // ---- the confirmed email capture (server-rendered marker; not on a reload)
  // Shopify marks EVERY customer form on the page as posted after any
  // customer post, so several markers can render for one signup. The
  // redirect keeps the posted form's id as the URL fragment; that names
  // the real one. One post, one event.
  const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (!nav || nav.type !== 'reload') {
    const markers = [...document.querySelectorAll('[data-notch-captured]')];
    const posted = markers.find((el) => { const f = el.closest('form'); return f && f.id && location.hash === '#' + f.id; }) || markers[0];
    if (posted) publish('notch:email_captured', { location: posted.dataset.notchCaptured }, posted);
  }

  // ---- sections seen, with dwell, published when the page hides
  const seen = new Map();
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      const now = Date.now();
      entries.forEach((x) => {
        const s = seen.get(x.target) || { ms: 0, since: 0 };
        if (x.isIntersecting) s.since = s.since || now;
        else if (s.since) { s.ms += now - s.since; s.since = 0; }
        seen.set(x.target, s);
      });
    }, { threshold: 0.5 });
    document.querySelectorAll('.shopify-section').forEach((el) => io.observe(el));
  }
  const reported = new Set();
  const bucket = (ms) => (ms < 1000 ? '<1s' : ms < 5000 ? '1-5s' : ms < 15000 ? '5-15s' : '15s+');
  function reportSections() {
    const now = Date.now();
    const all = [...document.querySelectorAll('.shopify-section')];
    const sections = [];
    seen.forEach((s, el) => {
      const ms = s.ms + (s.since ? now - s.since : 0);
      // A closed <dialog> or an empty group wrapper has no height and still
      // reads as intersecting; it was not seen.
      if (!ms || reported.has(el) || el.getBoundingClientRect().height < 40) return;
      reported.add(el);
      sections.push(Object.assign(sectionOf(el.firstElementChild || el), { dwell_bucket: bucket(ms), position: all.indexOf(el) + 1 }));
    });
    if (sections.length) publish('notch:section_viewed', { sections }, null);
  }
  // Reported when the page hides, before a held navigation, and every 10s,
  // because a report made during unload never reaches the pixel. A section is
  // reported once, with its dwell at that moment.
  // The pixel maps notch:page_hidden to $pageleave. Fire on each hide, re-arm on
  // show, so the last hide before the exit carries the leave time; latching once
  // would log a tab-switch as the leave. visibilitychange reaches the sandboxed
  // pixel; a real unload would not.
  let pageHidden = false;
  const onHide = () => { reportSections(); if (!pageHidden) { pageHidden = true; publish('notch:page_hidden', {}, null); } };
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') onHide(); else pageHidden = false; });
  window.addEventListener('pagehide', onHide);
  window.addEventListener('pageshow', () => { pageHidden = false; });
  setInterval(reportSections, 10000);

  waitForBus();
})();
